import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ReportUtils} from '../../../shared/libraries/report-utils';
import {Observable, Subscription, withLatestFrom} from 'rxjs';
import {FormGroup} from '@angular/forms';
import {filter, map} from 'rxjs/operators';
import {ActivatedRoute, Data} from "@angular/router";
import {AdRComparison, AdRequestOptions, AdRequestParams} from '../../../shared/interfaces/ad-reports';
import {MatSnackBar} from "@angular/material/snack-bar";
import {TranslateService} from "@ngx-translate/core";
import {AppState} from "../../../shared/store/store";
import {Store} from "@ngrx/store";
import {DimensionComponentObject, DimensionComponentObjects} from '../../../shared/classes/dimension-component-object';
import {MetricComponentObject, MetricComponentObjects} from '../../../shared/classes/metric-component-object';
import {ReportComponent} from "../../../shared/components/report/report.component";
import {selectDimensionComponentObjects, selectMetricComponentObjects} from '../../../shared/store/selectors';
import {ReportStore} from '../../../shared/store/report/report.store';
import {ReportTableStore} from '../../../shared/store/report/report-table.store';
import {PublicReportData} from '../../../shared/store/publicReports/public-reports';
import {ReportParamsStore} from '../../../shared/store/report/report-params.store';
import {ParamsConfigsService} from '../../../shared/services/params-configs.service';

@Component({
  selector: 'app-ad-reports',
  templateUrl: './ad-reports.component.html',
  styleUrls: ['./ad-reports.component.scss'],
  providers: [
    ReportStore,
    ReportParamsStore,
    ReportTableStore
  ]
})
export class AdReportsComponent implements OnInit, OnDestroy {
  @ViewChild(ReportComponent) reportComponent: ReportComponent;

  @Input('publicReportData') set setReport(publicReportData: PublicReportData | null | undefined) {
    if (publicReportData) {
      this._reportStore.initPublicReport(
        publicReportData.report,
        publicReportData.uuid,
        publicReportData.token
      );
    }
  };

  public readonly form: FormGroup = this._reportParamsStore.form;

  private readonly _metrics$: Observable<MetricComponentObjects> = this._store.select(selectMetricComponentObjects);
  private readonly _dimensions$: Observable<DimensionComponentObjects> = this._store.select(selectDimensionComponentObjects);

  public readonly requestParams$: Observable<Partial<AdRequestParams>> = this._reportStore.requestParams$;
  public readonly requestOptions$: Observable<Partial<AdRequestOptions>> = this._reportStore.requestOptions$;
  public readonly limit$: Observable<number> = this._reportStore.limit$;

  public readonly iframe$: Observable<boolean> = this._route.data.pipe(
    map((data: Data): boolean => data.iframe || false)
  );

  private conversionMetricSubs: Subscription;
  private compareSubs: Subscription;

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _snackBar: MatSnackBar,
    private readonly _translateS: TranslateService,
    private readonly _store: Store<AppState>,
    private readonly _reportStore: ReportStore,
    private readonly _reportParamsStore: ReportParamsStore,
    private readonly _paramsConfigsService: ParamsConfigsService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    ReportUtils.unsubscribe(this.conversionMetricSubs);
    ReportUtils.unsubscribe(this.compareSubs);
  }

  public onReady(): void {
    switch (this._route.snapshot.data.reportType) {
      case 'cycle':
        ReportUtils.unsubscribe(this.conversionMetricSubs);
        this.conversionMetricSubs = this.form.get('conversion_metric').valueChanges
          .pipe(
            withLatestFrom(this._metrics$),
            filter(([conversion_metric, metrics]: [MetricComponentObject, MetricComponentObjects]) => !!conversion_metric && !!metrics),
          )
          .subscribe(([conversion_metric, metrics]: [MetricComponentObject, MetricComponentObjects]): void => {
            const cycleMetrics: Array<string> = this._paramsConfigsService.getCycleMetricsTracking();
            const values: Array<MetricComponentObject> = this.form.get('metrics').value;
            const updatedMetricsWithAttributedMetric: Array<MetricComponentObject> = [];

            for (const metricSlug of cycleMetrics) {
              if (metricSlug === 'attr-conversion-bepl') {
                const newMetric: MetricComponentObject = metrics.find((met: MetricComponentObject): boolean =>
                  met.payload.attributes.slug === conversion_metric.payload.attributes.slug);
                if (newMetric) {
                  updatedMetricsWithAttributedMetric.push(newMetric);
                }
              } else {
                const originalMetric: MetricComponentObject = metrics.find((met: MetricComponentObject): boolean =>
                  met.payload.attributes.slug === metricSlug);
                if (originalMetric) {
                  updatedMetricsWithAttributedMetric.push(originalMetric);
                }
              }
            }
            this.form.get('metrics').setValue(updatedMetricsWithAttributedMetric);
          });
        break;
      case 'performance':
        const disableOptions: Array<string> = ['day', 'week', 'month', 'year'];

        ReportUtils.unsubscribe(this.compareSubs);
        this.compareSubs = this.form.get('compare').valueChanges
          .pipe(
            filter((comparison: AdRComparison): boolean => !!comparison),
            withLatestFrom(this._dimensions$)
          )
          .subscribe(([comparison, dimensions]: [AdRComparison, Array<DimensionComponentObject>]) => {
            if (comparison.on === 'period') {
              const disabledDimensions: Array<DimensionComponentObject> = dimensions.filter((option: DimensionComponentObject) => disableOptions.includes(option.payload.attributes.slug));
              const dimensionsWithoutDate: Array<DimensionComponentObject> = this.form.get('dimensions').value
                ?.filter((option: DimensionComponentObject) => !disableOptions.includes(option.payload.attributes.slug));

              if (dimensionsWithoutDate && this.form.get('dimensions').value.length !== dimensionsWithoutDate.length) {
                this._snackBar.open(this._translateS.instant('reports.removed_date_type_dimensions_message'), null, {duration: 10000});
              }
              // set
              this._reportStore.setDisabledValues('dimensions', disabledDimensions);
            } else {
              // reset
              this._reportStore.setDisabledValues('dimensions', []);
            }
          });
        break;
    }
  }

}
